/**
 * Web-only headline copy for a held pack; pack state (status label,
 * spendable, ceiling, split, tombstone) is `@butter/library/lessons`.
 *
 * @module pages/lessons/lessonPackDisplay
 */

import {
  isLessonPackUnlimited,
  lessonMinutesToHours,
  lessonPackCeilingMinutes,
  lessonPackName,
  lessonPackUsedMinutes,
} from '@butter/library/lessons';
import { formatHours } from '@butter/library/shared';
import type { UsageLimit } from '@butter/ui/domains/subscriptions';
import { formatLessonHours } from '~/services/internalApi/lessons';
import type { UserLesson } from '~/services/internalApi/lessons/types';

/** `null` = unlimited, `0` = spent — never "0", never blank, never an absent row. */
export function formatRemaining(remainingMinutes: number | null): string {
  if (remainingMinutes === null) return 'Unlimited';
  if (remainingMinutes === 0) return 'None left';
  return formatLessonHours(remainingMinutes);
}

/**
 * The pack card's headline figure. `formatRemaining`'s 'Unlimited' and
 * 'None left' already read as complete phrases; only a finite duration
 * takes the ' left' suffix ("3.3 Hours left"). Branches on the raw
 * minutes, never on the rendered string — a copy change to `formatRemaining`
 * (e.g. "None left" → "No time left") can't desync this from it the way a
 * string comparison against a literal copy could.
 */
export function formatRemainingHeadline(remainingMinutes: number | null): string {
  const label = formatRemaining(remainingMinutes);
  return remainingMinutes === null || remainingMinutes === 0 ? label : `${label} left`;
}

/**
 * Usage-panel rows for a held pack. Used, total and remaining all take one
 * decimal ("1.0 / 5.0h used · 4.0h left"). Unlimited when the library says
 * so — a tracked `remainingMinutes` with no ceiling reads as unlimited.
 */
export function lessonUsageRows(pack: UserLesson): UsageLimit[] {
  if (isLessonPackUnlimited(pack)) {
    return [{ label: lessonPackName(pack), unlimited: true }];
  }
  const usedMinutes = lessonPackUsedMinutes(pack)!;
  const ceilingMinutes = lessonPackCeilingMinutes(pack)!;
  return [
    {
      label: lessonPackName(pack),
      used: usedMinutes,
      total: ceilingMinutes,
      usedLabel: formatHours(lessonMinutesToHours(usedMinutes)),
      totalLabel: formatHours(lessonMinutesToHours(ceilingMinutes)),
      remainingLabel: formatHours(lessonMinutesToHours(pack.remainingMinutes!)),
    },
  ];
}
